import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Ticket, Download, Send, Play, Pause, X, Loader2, Printer, TrendingUp } from "lucide-react";
import { sr } from "../../lib/sr";

const dlBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const TYPES = [
  ["advertising_credit", "Advertising Credit", "stored_value"],
  ["egift_card", "eGift Card", "stored_value"],
  ["service_credit", "Service Credit", "stored_value"],
  ["channel_credit", "Channel-Specific Credit", "stored_value"],
  ["fixed_coupon", "Fixed-Dollar Coupon", "fixed"],
  ["percentage_coupon", "Percentage Discount", "percentage"],
  ["subscription_promo", "Subscription Discount", "fixed"],
  ["setup_fee_waiver", "Setup-Fee Waiver", "fixed"],
  ["first_campaign", "First-Campaign Promotion", "percentage"],
  ["reactivation", "Returning / Reactivation Offer", "stored_value"],
  ["referral", "Referral Promotion", "stored_value"],
  ["event_offer", "Event / Trade-Show Offer", "stored_value"],
];
const ELIGIBILITY = ["any", "new_account", "first_business", "first_campaign", "returning", "reactivation", "referral", "trade_show"];
const IDENTITY = ["business", "user", "billing_account", "location", "franchise_location", "agency", "agency_client"];
const PRODUCTS = ["any", "Advertising Campaign", "CTV", "Online Video", "Display", "Native", "Audio", "DOOH", "Creative Services", "Managed Services", "Setup Fee", "Subscription"];

const blank = {
  name: "", customer_description: "", type: "advertising_credit", value_type: "stored_value", value: 500,
  max_discount: "", minimum_spend: 0, benefit_mode: "price_discount", eligible_products: ["any"],
  eligibility_type: "any", redemption_identity_type: "business", max_per_identity: 1, max_total_redemptions: "",
  stackable: false, expiration_mode: "days_after_claim", expiration_days: 60, expires_at: "",
  code_prefix: "SR", initial_codes: 0, shared_code: "", source_name: "", assigned_salesperson: "", status: "active",
};

const money = (v) => `$${Number(v || 0).toLocaleString()}`;

export default function PlatformPromotions() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(null);
  const [codes, setCodes] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [showFunnel, setShowFunnel] = useState(false);

  const openFunnel = async () => {
    setShowFunnel(true);
    try { setFunnel(await sr.ppFunnel()); } catch { /* noop */ }
  };
  const printCards = async (p) => {
    try { dlBlob(await sr.ppCardsPdf(p.id, 50), `promo-${p.id.slice(0, 8)}-cards.pdf`); }
    catch { toast.error("Could not generate cards PDF"); }
  };
  const exportCsv = async (p) => {
    try { dlBlob(await sr.ppExportCsv(p.id), `promo-${p.id.slice(0, 8)}-codes.csv`); }
    catch { toast.error("Could not export CSV"); }
  };

  const load = async () => {
    try {
      const [l, s] = await Promise.all([sr.ppList(), sr.ppSummary()]);
      setRows(l.promotions || []);
      setSummary(s);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load promotions"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const pickType = (e) => {
    const t = TYPES.find((x) => x[0] === e.target.value);
    setForm((f) => ({ ...f, type: t[0], value_type: t[2] }));
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = { ...form };
      body.value = Number(body.value);
      body.max_discount = body.max_discount === "" ? null : Number(body.max_discount);
      body.minimum_spend = Number(body.minimum_spend || 0);
      body.max_per_identity = Number(body.max_per_identity || 1);
      body.max_total_redemptions = body.max_total_redemptions === "" ? null : Number(body.max_total_redemptions);
      body.initial_codes = Number(body.initial_codes || 0);
      body.expiration_days = body.expiration_days === "" ? null : Number(body.expiration_days);
      if (!body.shared_code) delete body.shared_code;
      await sr.ppCreate(body);
      toast.success("Promotion created");
      setCreating(false); setForm(blank); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
    finally { setSaving(false); }
  };

  const openCodes = async (p) => {
    setSel(p);
    try { const r = await sr.ppCodes(p.id); setCodes(r.codes || []); } catch { setCodes([]); }
  };
  const gen = async (n) => {
    try { const r = await sr.ppGenCodes(sel.id, n); toast.success(`${r.created} codes generated`); openCodes(sel); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
  };
  const toggleStatus = async (p) => {
    try { p.status === "active" ? await sr.ppPause(p.id) : await sr.ppActivate(p.id); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }} data-testid="admin-promotions">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div className="sr-eyebrow">Administration</div>
          <h1 style={{ fontSize: 26, margin: "4px 0 0" }}>Promotions &amp; Credits</h1>
          <p className="sr-muted" style={{ fontSize: 14 }}>Issue SmartReach gift cards, advertising credits and coupons to your business customers.</p>
        </div>
        <button className="sr-btn sr-btn-primary" onClick={() => setCreating(true)} data-testid="promo-new-btn"><Plus size={16} /> Create promotion</button>
        <button className="sr-btn" onClick={openFunnel} data-testid="promo-funnel-btn"><TrendingUp size={16} /> Analytics</button>
      </div>

      {showFunnel && funnel && (
        <div className="sr-card" style={{ marginTop: 20 }} data-testid="promo-funnel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Offer → Business → Campaign → Revenue</h3>
            <button className="sr-btn sr-btn-sm" onClick={() => setShowFunnel(false)}><X size={14} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10, marginTop: 12 }}>
            {funnel.funnel.map((f) => (
              <div key={f.step} className="sr-card" style={{ padding: 12, background: "#f9fafb" }} data-testid={`funnel-step-${f.step.split(" ")[0].toLowerCase()}`}>
                <div className="sr-muted" style={{ fontSize: 12 }}>{f.step}</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{f.count}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 12, flexWrap: "wrap" }}>
            <div><span className="sr-muted" style={{ fontSize: 12 }}>Value issued</span><div style={{ fontWeight: 800 }}>{money(funnel.value_issued)}</div></div>
            <div><span className="sr-muted" style={{ fontSize: 12 }}>Value redeemed (revenue-linked)</span><div style={{ fontWeight: 800 }}>{money(funnel.value_redeemed)}</div></div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16, marginTop: 16 }}>
            {[["By source / event", funnel.by_source], ["By salesperson", funnel.by_salesperson], ["By promotion type", funnel.by_type]].map(([title, list]) => (
              <div key={title}>
                <div className="sr-eyebrow" style={{ fontSize: 12, marginBottom: 6 }}>{title}</div>
                {(list || []).length ? list.map((r) => (
                  <div key={r.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "3px 0" }}>
                    <span>{r.key}</span><span className="sr-muted">{r.claims} · {money(r.value)}</span>
                  </div>
                )) : <div className="sr-muted" style={{ fontSize: 13 }}>No data yet</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginTop: 20 }} data-testid="promo-summary">
          {[["Active", summary.active_promotions], ["Issued", money(summary.value_issued)], ["Outstanding", money(summary.available_outstanding)], ["Held", money(summary.held)], ["Redeemed", money(summary.value_redeemed)], ["Businesses", summary.businesses_acquired]].map(([l, v]) => (
            <div key={l} className="sr-card" style={{ padding: 14 }}>
              <div className="sr-muted" style={{ fontSize: 12 }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "var(--navy,#0A121C)" }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="sr-card" style={{ marginTop: 20, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} data-testid="promo-table">
          <thead><tr style={{ background: "#f6f7f9", textAlign: "left" }}>
            {["Promotion", "Type", "Value", "Eligibility", "Codes", "Claims", "Status", ""].map((h) => <th key={h} style={{ padding: "10px 12px" }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} style={{ borderTop: "1px solid #eee" }} data-testid={`promo-row-${p.id}`}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{p.name}</td>
                <td style={{ padding: "10px 12px" }}>{p.type}</td>
                <td style={{ padding: "10px 12px" }}>{p.value_type === "percentage" ? `${p.value}%` : money(p.value)}</td>
                <td style={{ padding: "10px 12px" }}>{p.eligibility_type}</td>
                <td style={{ padding: "10px 12px" }}>{p.codes}</td>
                <td style={{ padding: "10px 12px" }}>{p.claims}</td>
                <td style={{ padding: "10px 12px" }}><span className="sr-badge">{p.status}</span></td>
                <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                  <button className="sr-btn sr-btn-sm" onClick={() => openCodes(p)} data-testid={`promo-codes-${p.id}`}><Ticket size={14} /> Codes</button>{" "}
                  <button className="sr-btn sr-btn-sm" onClick={() => toggleStatus(p)}>{p.status === "active" ? <Pause size={14} /> : <Play size={14} />}</button>{" "}
                  <button className="sr-btn sr-btn-sm" onClick={() => exportCsv(p)} data-testid={`promo-export-${p.id}`}><Download size={14} /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#888" }}>No promotions yet — create your first one.</td></tr>}
          </tbody>
        </table>
      </div>

      {creating && (
        <div style={overlay} onClick={() => setCreating(false)}>
          <div className="sr-card" style={modal} onClick={(e) => e.stopPropagation()} data-testid="promo-create-form">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>Create promotion</h3>
              <button className="sr-btn sr-btn-sm" onClick={() => setCreating(false)}><X size={16} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14 }}>
              <Field label="Promotion name" full><input className="sr-input" value={form.name} onChange={set("name")} data-testid="promo-name" /></Field>
              <Field label="Customer-facing description" full><input className="sr-input" value={form.customer_description} onChange={set("customer_description")} /></Field>
              <Field label="Type"><select className="sr-select" value={form.type} onChange={pickType} data-testid="promo-type">{TYPES.map((t) => <option key={t[0]} value={t[0]}>{t[1]}</option>)}</select></Field>
              <Field label={form.value_type === "percentage" ? "Percent (%)" : "Value ($)"}><input className="sr-input" type="number" value={form.value} onChange={set("value")} data-testid="promo-value" /></Field>
              {form.value_type === "percentage" && <Field label="Max discount ($)"><input className="sr-input" type="number" value={form.max_discount} onChange={set("max_discount")} /></Field>}
              <Field label="Minimum spend ($)"><input className="sr-input" type="number" value={form.minimum_spend} onChange={set("minimum_spend")} /></Field>
              <Field label="Benefit mode"><select className="sr-select" value={form.benefit_mode} onChange={set("benefit_mode")}><option value="price_discount">Price discount</option><option value="bonus_value">Bonus value</option></select></Field>
              <Field label="Eligibility"><select className="sr-select" value={form.eligibility_type} onChange={set("eligibility_type")}>{ELIGIBILITY.map((e) => <option key={e} value={e}>{e}</option>)}</select></Field>
              <Field label="Uniqueness / redemption basis"><select className="sr-select" value={form.redemption_identity_type} onChange={set("redemption_identity_type")} data-testid="promo-identity">{IDENTITY.map((e) => <option key={e} value={e}>1 per {e}</option>)}</select></Field>
              <Field label="Max per identity"><input className="sr-input" type="number" value={form.max_per_identity} onChange={set("max_per_identity")} /></Field>
              <Field label="Expiration"><select className="sr-select" value={form.expiration_mode} onChange={set("expiration_mode")}><option value="days_after_claim">Days after claim</option><option value="days_after_issue">Days after issue</option><option value="fixed_date">Fixed date</option></select></Field>
              {form.expiration_mode === "fixed_date"
                ? <Field label="Expires on"><input className="sr-input" type="date" value={form.expires_at} onChange={set("expires_at")} /></Field>
                : <Field label="Days"><input className="sr-input" type="number" value={form.expiration_days} onChange={set("expiration_days")} /></Field>}
              <Field label="Code prefix"><input className="sr-input" value={form.code_prefix} onChange={set("code_prefix")} /></Field>
              <Field label="Initial unique codes"><input className="sr-input" type="number" value={form.initial_codes} onChange={set("initial_codes")} data-testid="promo-initial-codes" /></Field>
              <Field label="Shared code (optional)"><input className="sr-input" placeholder="e.g. SMALLBIZ20" value={form.shared_code} onChange={set("shared_code")} /></Field>
              <Field label="Assigned salesperson"><input className="sr-input" value={form.assigned_salesperson} onChange={set("assigned_salesperson")} /></Field>
              <Field full><label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}><input type="checkbox" checked={form.stackable} onChange={set("stackable")} /> Stackable with other promotions</label></Field>
            </div>
            <button className="sr-btn sr-btn-primary" style={{ marginTop: 16 }} onClick={create} disabled={saving} data-testid="promo-save">
              {saving ? <Loader2 size={15} className="sr-spin" /> : <Plus size={15} />} Create promotion
            </button>
          </div>
        </div>
      )}

      {sel && (
        <div style={overlay} onClick={() => setSel(null)}>
          <div className="sr-card" style={modal} onClick={(e) => e.stopPropagation()} data-testid="promo-codes-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>{sel.name} — codes</h3>
              <button className="sr-btn sr-btn-sm" onClick={() => setSel(null)}><X size={16} /></button>
            </div>
            <div style={{ display: "flex", gap: 8, margin: "12px 0", flexWrap: "wrap" }}>
              {[10, 25, 50, 100, 250, 500].map((n) => <button key={n} className="sr-btn sr-btn-sm" onClick={() => gen(n)} data-testid={`promo-gen-${n}`}>+{n}</button>)}
              <button className="sr-btn sr-btn-sm" onClick={() => exportCsv(sel)} data-testid="promo-export-csv"><Download size={14} /> Export CSV</button>
              <button className="sr-btn sr-btn-sm" onClick={() => printCards(sel)} data-testid="promo-print-cards"><Printer size={14} /> Print cards (PDF)</button>
            </div>
            <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid #eee", borderRadius: 8 }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#f6f7f9", textAlign: "left" }}><th style={{ padding: 8 }}>Code</th><th style={{ padding: 8 }}>Value</th><th style={{ padding: 8 }}>Status</th></tr></thead>
                <tbody>{codes.map((c) => <tr key={c.id} style={{ borderTop: "1px solid #eee" }}><td style={{ padding: 8, fontFamily: "monospace" }}>{c.code}</td><td style={{ padding: 8 }}>{money(c.value)}</td><td style={{ padding: 8 }}>{c.status}</td></tr>)}</tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, full }) => (
  <div className="sr-field" style={full ? { gridColumn: "1 / -1" } : {}}>
    {label && <label className="sr-label">{label}</label>}
    {children}
  </div>
);
const overlay = { position: "fixed", inset: 0, background: "rgba(10,18,28,.5)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, zIndex: 100, overflow: "auto" };
const modal = { width: 720, maxWidth: "100%", padding: 22 };
